 
import { useWasteStore } from "../../../../store/store";
import { type ChangeEvent, useState, useEffect } from "react";
import { useUserData } from "@hooks/useUserData";
import { NavigationButtons } from "@molecules/NavigationButtons/NavigationButtons";
import { loginRequest, msalInstance } from "@utils/authConfig";
import { useNavigate } from "react-router-dom";
import { TextInputMolecule } from "@molecules/TextInput/TextInput";
import { ExternalLink } from "@atoms/ExternalLink/ExternalLink";
import { RoutesList } from "../../../../routes/Routes";
import {
  customerFirstName,
  customerLastName,
  customerEmail,
  readableZodValidationError,
} from "@utils/validations/yourDetails";
import { useSessionStorage } from "@hooks/useSessionStorage";
import { EventMessage, EventType } from "@azure/msal-browser";
import { useHandleSubmit } from "@hooks/useHandleSubmit";
import { useWastePayload } from "@hooks/usePayload";
import { FormTitle } from "@components/atoms/FormTitle/FormTitle";

export const YourDetails = () => {
  const navigate = useNavigate();
  const setCrmId = useSessionStorage("crmId");
  const [isMsalRunning, setIsMsalRunning] = useState(false);
  const { isLoggedInUser, contactData, isLoading, isFetching } = useUserData(sessionStorage);

  const {
    firstName,
    lastName,
    email,
    updateFirstName,
    updateLastName,
    updateEmail,
  } = useWasteStore();
  
  const payload = useWastePayload();
  const { mutate, isLoading: dataLoading } = useHandleSubmit();

  useEffect(() => {
    updateFirstName(contactData?.firstname ?? "");
    updateLastName(contactData?.lastname ?? "");
    updateEmail(contactData?.emailaddress1 ?? "");
  }, [contactData]);


  useEffect(() => {
    setIsMsalRunning(true);
    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        setCrmId[1](msalInstance.getActiveAccount()?.idTokenClaims?.extension_CrmContactId as string || "");
      }
    });
    setIsMsalRunning(false);
  }, [])

  const dataProtectionLink = (
    <ExternalLink
      href={
        "https://www.westminster.gov.uk/data-protection/fair-processing-notice"
      }
    >
      Westminster City Council&apos;s data protection notice.
    </ExternalLink>
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [errorPath, setErrorPath] = useState("");

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (errorPath === e.target.name) {
      resetErrorState();
    }
    if (e.target.name === "firstName") {
      updateFirstName(e.target.value);
    }
    if (e.target.name === "lastName") {
      updateLastName(e.target.value);
    }
    if (e.target.name === "email") {
      updateEmail(e.target.value);
    }
  };

  function resetErrorState() {
    setErrorMessage("");
    setErrorPath("");
  }

  const handleSubmit = () => {
    try {
      customerFirstName.parse({ firstName });
      customerLastName.parse({ lastName });
      customerEmail.parse({ email });
      mutate(payload);
    }
    catch (err) {
      setErrorMessage(
        Object.values(readableZodValidationError(err))[0][0].message,
      );
      setErrorPath(
        Object.values(readableZodValidationError(err))[0][0].path[0],
      );
      return false;
    }
  };

  return (
    <main className="body-1">
      {isLoading || isFetching || isMsalRunning ? (
        <>Loading user data...</>
      ) : (
        <>
          <div className="mb-6">
          <FormTitle text="3. Your details" />
            {(isLoggedInUser !== true) && (
              <p className="body-1 mb-1">
                Type in your details below if you would like to receive updates or
                log in/sign up to your{" "}
                <a
                  className="text-link-colour underline hover:text-link-hover-colour"
                  onClick={async () => {

                    try {
                      if (msalInstance.getAllAccounts().length > 0) {
                        navigate(RoutesList.YOUR_DETAILS);
                      } else {
                        await msalInstance.loginRedirect(loginRequest);
                      }
                    } catch (error) {
                      throw new Error(
                        "Your login request failed Please refresh the page and try again",
                      );
                    }
                  }}
                >
                  MyWestminster account
                </a>
              </p>)
            }
          </div>

          <form className="flex flex-col gap-y-2 pb-20">
            <div>
              <TextInputMolecule
                label="First name (required)*"
                name="firstName"
                itemID="firstName"
                disabled={isLoggedInUser}
                value={firstName}
                type="text"
                onChange={(e) => handleChange(e)}
                inlineerrormessage={errorPath === "firstName" ? errorMessage : ""}
              />
            </div>
            <div>
              <TextInputMolecule
                label="Last name (required)*"
                name="lastName"
                itemID="lastName"
                disabled={isLoggedInUser}
                value={lastName}
                type="text"
                onChange={(e) => handleChange(e)}
                inlineerrormessage={errorPath === "lastName" ? errorMessage : ""}
              />
            </div>

            <div>
              <TextInputMolecule
                label="Email (required)*"
                name="email"
                itemID="email"
                disabled={isLoggedInUser}
                value={email}
                type="text"
                onChange={(e) => handleChange(e)}
                inlineerrormessage={errorPath === "email" ? errorMessage : ""}
              />
            </div>

            <div className="my-4">
              If you would like to know more about how the council processes your
              data, please see {dataProtectionLink}
            </div>

            <NavigationButtons
              next={handleSubmit}
              previous={() => {
                navigate(RoutesList.WASTE_DETAILS);
              }}
              isLoading={dataLoading}
              primaryButtonText="Submit"
              secondaryButtonText="Previous"
            />
          </form>
        </>
      )}
    </main>
  );
};
